import {createExperimentAnalytics} from './offer-split-analytics.js';
import {EXPERIMENT,TTL,permission,eligible,valid,allocate,orderPayload} from './offer-split-core.js';

const el=document.getElementById('mg-offer-split-config');
if(el) start(JSON.parse(el.textContent)).catch(()=>document.documentElement.removeAttribute('data-mg-offer-pending'));

async function start(config) {
  if(window.MobilegsOfferSplit||!config.enabled)return;
  const url=new URL(location.href), qa=config.qa===true;
  if(!qa&&url.searchParams.has('mg_qa'))return;
  // QA-only template never enrolls shoppers; force-arm is unavailable live.
  if(qa&&url.searchParams.get('mg_qa')!=='1'){
    let prior;try{prior=JSON.parse(localStorage.getItem('mg_offer_split_v1_qa'));}catch{}
    if(!valid(prior,Date.now(),true)||!prior.exposed)return;
  }
  const key=`mg_offer_split_v1${qa?'_qa':''}`, attribute='__mg_offer_assignment';
  let context=null, active=true, generation=0, syncPromise=null, lastSynced=null;
  let clarityQueue=[], clarityTimer=null, clarityAttempts=0;
  const consent=()=>permission(window.Shopify?.customerPrivacy);
  const read=()=>{try{return JSON.parse(localStorage.getItem(key));}catch{return null;}};
  const save=value=>localStorage.setItem(key,JSON.stringify(value));
  const notify=(event,detail={})=>document.dispatchEvent(new CustomEvent('mobilegs:offer-split',{detail:{event,...detail}}));
  const diagnostics=[];
  const status=(event,detail={})=>{if(qa){diagnostics.push({event,ms:Math.round(performance.now()),...detail});document.documentElement.dataset.mgOfferStatus=event;}notify(event,detail);};
  window.MobilegsOfferSplit={get:()=>context?{...context}:null,diagnostics,sync};
  const analytics=createExperimentAnalytics({allowed:()=>active&&!!context&&consent()==='allowed',getTag:()=>window.gtag,getContext:()=>context,measurementId:config.ga4MeasurementId,status});
  const track=name=>{clarity('event',name);analytics.event(name);};
  status('module-ready');
  const apiReady=new Promise(resolve=>{
    if(window.Shopify?.customerPrivacy)return resolve();
    window.Shopify?.loadFeatures?.([{name:'consent-tracking-api',version:'0.1'}],resolve);
    setTimeout(resolve,1200);
  });
  const clearOwned=async()=>{
    const had=read();context=null;active=false;generation++;lastSynced=null;
    clarityQueue=[];clearTimeout(clarityTimer);analytics.clear();
    try{localStorage.removeItem(key);}catch{}
    if(syncPromise)await syncPromise;
    if(had){const cleared=await updateCart('');status(cleared?'cart-attribution-cleared':'cart-attribution-clear-failed');}
    status('consent-cleared');
  };
  if(qa)window.MobilegsOfferSplit.clearQA=clearOwned;
  async function updateCart(value) {
    // No remote commerce mutations from an isolated local preview.
    if(config.localPreview){status('preview-cart-attribute',{value});return true;}
    const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),8000);
    try {
      const root=window.Shopify?.routes?.root||'/';
      const response=await fetch(root+'cart/update.js',{method:'POST',credentials:'same-origin',signal:controller.signal,
        headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({attributes:{[attribute]:value}})});
      return response.ok;
    } catch {return false;} finally {clearTimeout(timer);}
  }
  async function sync() {
    if(!active||consent()!=='allowed'||!context)return false;
    if(!valid(context,Date.now(),qa)){await clearOwned();return false;}
    const value=orderPayload(context);if(!value)return false;
    if(value===lastSynced)return true;
    // Serialize only this adapter; don't interfere with existing attribution.
    if(syncPromise){await syncPromise;return sync();}
    syncPromise=updateCart(value);
    const ok=await syncPromise;syncPromise=null;
    if(ok&&active&&consent()==='allowed')lastSynced=value;
    status(ok?'cart-attribution-ready':'cart-attribution-failed');return ok;
  }
  document.addEventListener('visitorConsentCollected',()=>{
    if(consent()==='denied')void clearOwned();
    // Never late-swap a page after the shopper has started interacting.
  });
  // The initial-response route can start independently of unrelated deferred
  // scripts, but its parser bootstrap remains the sole allocation authority.
  if(config.serverTreatment)await window.__mgOfferInitial?.ready;
  await apiReady;
  status('consent-ready');
  if(consent()!=='allowed'){if(consent()==='denied')await clearOwned();status('unassigned-consent');return;}
  const source=eligible(url.href,location.origin);
  if(!source) {
    const saved=read();
    if(valid(saved,Date.now(),qa)&&saved.exposed)context=saved;
    else {status('outside-cohort');return;}
    // Carry the same attribution on subsequent cart/pages; never new exposure.
    labelContext();bind();await sync();return;
  }
  if(config.serverTreatment) {
    // Allocation and first delivery already happened during HTML parsing.
    // Do not redraw, reallocate or retry a late treatment from the deferred module.
    const initial=window.__mgOfferInitial;
    const selected=await initial?.ready;
    if(qa&&initial?.events)diagnostics.unshift(...initial.events);
    if(!valid(selected,Date.now(),qa)||!selected.exposed){status('unassigned-initial');return;}
    context={...selected};
    labelContext();track('offer_page_exposed');
    if(context.fallback)track('offer_render_failed');
    status('exposed',{arm:context.arm,rendered:context.rendered,source:context.source,qa});
    if(context.rendered==='B') {
      try {
        await waitForNativeCommerce();status('native-globals-ready');bind();
        await Promise.all(config.scripts.map(loadScript));
        const main=document.querySelector('[data-mg-offer-server]');
        customElements.upgrade(main);initializeReviews(main);
        const product=main.querySelector('[id^="MainProduct-"]');product.inert=false;product.removeAttribute('aria-busy');
        document.getElementById('mg-offer-commerce-status')?.remove();
        status('commerce-ready');
        const anchor=location.hash&&document.getElementById(location.hash.slice(1));if(anchor)anchor.scrollIntoView();
      }catch(e){
        const pending=document.getElementById('mg-offer-commerce-status');
        if(pending?.firstChild)pending.firstChild.textContent='Style options are taking longer to load. ';
        status('commerce-failed',{reason:e.message});track('offer_render_failed');
      }
    } else {
      try {await waitForNativeCommerce();bind();}catch(e){status('native-globals-failed',{reason:e.message});}
    }
    await sync();return;
  }
  // Web Locks prevents simultaneous tabs drawing conflicting first assignments.
  // Browsers without it remain outside the test; shopping still works.
  if(!navigator.locks?.request){status('unassigned-lock-unavailable');return;}
  try {
    context=await navigator.locks.request(key,()=>{
      const next=allocate({saved:read(),now:Date.now(),qa,source:source.key,bytes:crypto.getRandomValues(new Uint8Array(17)),
        forcedArm:qa?url.searchParams.get('mg_preview_arm'):undefined});
      // Do not sync a previous exposure while the next page is being selected.
      delete next.exposed;delete next.rendered;save(next);return next;
    });
  } catch {status('unassigned-storage');return;}
  if(!active||consent()!=='allowed'){await clearOwned();return;}
  let rendered='A', failure=null, interacted=false;
  const markInteraction=()=>{interacted=true;};
  for(const event of ['pointerdown','keydown','wheel'])document.addEventListener(event,markInteraction,{capture:true,once:true});
  if(context.arm==='B'){
    const token=generation;
    try {await renderTreatment(config,()=>active&&generation===token&&consent()==='allowed'&&!interacted&&performance.now()<(window.__mgOfferDeadline||0),status);rendered='B';}
    catch(e){failure=e.message;status('treatment-failed',{reason:failure});}
  }
  for(const event of ['pointerdown','keydown','wheel'])document.removeEventListener(event,markInteraction,true);
  document.documentElement.removeAttribute('data-mg-offer-pending');
  if(!active||consent()!=='allowed')return;
  context={...context,rendered,exposed:Date.now(),fallback:!!failure};
  try{save(context);}catch{status('exposure-storage-failed');context=null;return;}
  document.documentElement.dataset.mgOfferArm=context.arm;
  document.documentElement.dataset.mgOfferRendered=rendered;
  status('exposed',{arm:context.arm,rendered,source:context.source,qa});
  labelContext();
  track('offer_page_exposed');if(failure)track('offer_render_failed');
  bind();await sync();

  function labelContext(){
  clarity('set','mg_experiment',EXPERIMENT);clarity('set','mg_assigned_arm',context.arm);
  clarity('set','mg_page_version',context.rendered==='B'?'reuse':'control');clarity('set','mg_campaign',context.source);
  clarity('set','mg_qa',qa?'yes':'no');clarity('set','mg_phase',qa?'qa':'active');
  clarity('set','mg_assignment_source','site_same_url');clarity('set','mg_crossover',context.crossSourceSeen?'yes':'no');
  }
  function flushClarity(){
    clarityTimer=null;
    if(!active||!context||consent()!=='allowed'){clarityQueue=[];return;}
    if(typeof window.clarity==='function'){for(const args of clarityQueue.splice(0)){window.clarity(...args);if(qa)status('clarity-call',{reason:args.slice(0,3).join(':')});}return;}
    if(++clarityAttempts<40)clarityTimer=setTimeout(flushClarity,500);
    else {clarityQueue=[];status('clarity-unavailable');}
  }
  function clarity(...args){
    if(!active||!context||consent()!=='allowed')return;
    clarityQueue.push(args);if(!clarityTimer)flushClarity();
  }
  function bind(){
    document.addEventListener('mobilegs:offer-viewer',e=>{if(['opened','loaded','failed','retry','photos'].includes(e.detail?.state))track('offer_3d_'+e.detail.state);});
    document.addEventListener('change',e=>{if(e.target.matches?.('product-form input[name="id"]'))track('offer_style_changed');});
    if(typeof subscribe==='function'&&typeof PUB_SUB_EVENTS!=='undefined')subscribe(PUB_SUB_EVENTS.cartUpdate,()=>{lastSynced=null;void sync();});
    if(location.pathname==='/cart')track('offer_cart_viewed');
  }
}

async function renderTreatment(config,stillAllowed,status=()=>{}) {
  if(!stillAllowed())throw Error('Render window already closed');
  status('treatment-request');
  const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),5000);
  let html;
  try{
    const target=new URL(config.treatmentPath,location.origin);
    if(target.origin!==location.origin||target.pathname!=='/products/mobilegs-ultra')throw Error('Invalid treatment source');
    const preview=new URL(location.href).searchParams.get('preview_theme_id');if(preview)target.searchParams.set('preview_theme_id',preview);
    const response=await fetch(target,{credentials:'same-origin',signal:controller.signal,headers:{Accept:'text/html'}});
    if(!response.ok)throw Error('Treatment unavailable');html=await response.text();
  }finally{clearTimeout(timer);}
  status('treatment-received');
  if(!stillAllowed())throw Error('Render window closed during response');
  const parsed=new DOMParser().parseFromString(html,'text/html');
  const main=parsed.getElementById('MainContent'),header=parsed.getElementById('shopify-section-offer-reuse-header'),style=parsed.getElementById('offer-reuse-styles');
  if(!main||!header||!style||!main.querySelector('mobilegs-pdp-viewer')||!main.querySelector('product-form'))throw Error('Incomplete treatment');
  // Ignore the fetched document head, pixels, customer data and inline code.
  // Only known native commerce assets from local config may execute.
  for(const root of [main,header])for(const s of root.querySelectorAll('script'))if(!['application/json','application/ld+json'].includes(s.type))s.remove();
  for(const root of [main,header])for(const duplicate of root.querySelectorAll('cart-notification, cart-drawer'))duplicate.remove();
  main.dataset.mgOfferB='';
  for(const n of main.querySelectorAll('product-info')){
    const replacement=parsed.createElement('offer-split-product-info');for(const a of n.attributes)replacement.setAttribute(a.name,a.value);
    replacement.append(...n.childNodes);n.replaceWith(replacement);
  }
  for(const root of [main,header])for(const n of root.querySelectorAll('*'))for(const a of Array.from(n.attributes))if(/^on/i.test(a.name))n.removeAttribute(a.name);
  const installed=[];
  try {
    const urls=[...main.querySelectorAll('link[rel="stylesheet"]'),...header.querySelectorAll('link[rel="stylesheet"]')].map(n=>n.href);
    for(const n of [...main.querySelectorAll('link[rel="stylesheet"]'),...header.querySelectorAll('link[rel="stylesheet"]')])n.remove();
    // Fetch scripts together while preserving execution order (async=false).
    // CSS and scripts are independent; no need to wait for one before the other.
    await Promise.all([
      Promise.all([...new Set(urls)].map(href=>loadStyle(href,installed))).then(()=>status('styles-ready')),
      Promise.all(config.scripts.map(loadScript)).then(()=>status('scripts-ready'))
    ]);
    if(!stillAllowed())throw Error('Render window closed or consent/interaction changed');
    const oldMain=document.getElementById('MainContent'),slot=document.getElementById('mg-offer-header-slot');
    if(!oldMain||!slot)throw Error('Missing integration mounts');
    document.head.append(style);installed.push(style);
    document.adoptNode(main);customElements.upgrade(main);
    document.adoptNode(header);customElements.upgrade(header);
    for(const sheet of slot.querySelectorAll('link[rel="stylesheet"]'))document.head.append(sheet);
    const cartUI=[...slot.querySelectorAll('cart-notification, cart-drawer')];
    slot.replaceChildren(header,...cartUI);oldMain.replaceWith(main);
    status('treatment-mounted');
    initializeReviews(main);
    // Inert fetched data becomes native custom elements only after insertion.
    for(const detail of main.querySelectorAll('details summary')){detail.setAttribute('role','button');detail.setAttribute('aria-expanded','false');detail.parentElement.addEventListener('toggle',()=>detail.setAttribute('aria-expanded',String(detail.parentElement.open)));}
    const anchor=location.hash&&document.getElementById(location.hash.slice(1));if(anchor)anchor.scrollIntoView();
  } catch(e){for(const n of installed)n.remove();throw e;}
}
function loadStyle(href,installed){
  const u=new URL(href,location.href);
  if(u.origin!==location.origin&&!(u.hostname==='mobilegs.com'||u.hostname==='cdn.shopify.com'||u.hostname==='d3hw6dc1ow8pp2.cloudfront.net'))return Promise.reject(Error('Unexpected stylesheet host'));
  if([...document.head.querySelectorAll('link[rel="stylesheet"]')].some(n=>n.href===u.href))return Promise.resolve();
  return new Promise((resolve,reject)=>{const n=document.createElement('link');n.rel='stylesheet';n.href=u.href;installed.push(n);
    const timer=setTimeout(()=>reject(Error('Stylesheet timeout')),4000);n.onload=()=>{clearTimeout(timer);resolve();};n.onerror=()=>{clearTimeout(timer);reject(Error('Stylesheet failed'));};document.head.append(n);});
}
function loadScript(src){
  const u=new URL(src,location.href);
  if(u.origin!==location.origin&&u.hostname!=='mobilegs.com'&&u.hostname!=='cdn.shopify.com')return Promise.reject(Error('Unexpected script host'));
  if([...document.head.querySelectorAll('script')].some(n=>n.src===u.href))return Promise.resolve();
  return new Promise((resolve,reject)=>{const n=document.createElement('script');n.async=false;n.src=u.href;
    const timer=setTimeout(()=>reject(Error('Script timeout')),4000);n.onload=()=>{clearTimeout(timer);resolve();};n.onerror=()=>{clearTimeout(timer);reject(Error('Script failed'));};document.head.append(n);});
}

// Okendo's official dynamic-widget example: okendo/okendo-reactjs-example/src/App.js.
function initializeReviews(root) {
  const widgets=[...root.querySelectorAll('[data-oke-widget], [data-oke-star-rating]')];
  if(!widgets.length)return;
  let complete=false;
  const init=()=>{
    if(complete||!root.isConnected||typeof window.okeWidgetApi?.initWidget!=='function')return;
    complete=true;document.removeEventListener('oke-script-loaded',init);
    for(const widget of widgets) {
      try{window.okeWidgetApi.initWidget(widget);}catch{widget.dataset.mgReviewInit='failed';}
    }
  };
  document.addEventListener('oke-script-loaded',init);init();
  setTimeout(()=>document.removeEventListener('oke-script-loaded',init),20000);
}

// Explicit dependency for the async coordinator; unrelated apps cannot block it.
function waitForNativeCommerce(){
  return new Promise((resolve,reject)=>{
    const deadline=performance.now()+10000;
    const poll=()=>{
      const cart=document.querySelector('cart-notification, cart-drawer');
      const cartReady=!cart||(typeof cart.getSectionsToRender==='function'&&typeof cart.setActiveElement==='function');
      if(window.__mgOfferNativeReady&&cartReady)return resolve();
      if(performance.now()>=deadline)return reject(Error('Native commerce initialization timeout'));
      setTimeout(poll,20);
    };poll();
  });
}
